import { hasConsent, onConsentChange } from '../util/cookieConsent';

export class LoggingAnalyticsHandler {
  trackPageView(url) {
    console.log('Analytics page view:', url);
  }
}

/**
 * Google Analytics 4 handler.
 *
 * Gated by analytics consent (UK GDPR / PECR).
 * The gtag.js script is injected dynamically only after consent is granted;
 * it is never loaded before that point.
 * Subscribes to onConsentChange so late-grant starts tracking immediately.
 */
export class GoogleAnalyticsHandler {
  constructor(googleAnalyticsId) {
    this.googleAnalyticsId = googleAnalyticsId;
    this.initialized = false;

    if (typeof window === 'undefined' || !googleAnalyticsId) return;

    if (hasConsent('analytics')) {
      this._init();
    }

    onConsentChange(record => {
      if (record.analytics && !this.initialized) {
        this._init();
      }
    });
  }

  _init() {
    if (this.initialized || typeof window === 'undefined' || !this.googleAnalyticsId) return;
    this.initialized = true;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', this.googleAnalyticsId, {
      cookie_flags: 'SameSite=None;Secure',
    });

    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.googleAnalyticsId}`;
    document.head.appendChild(script);
  }

  trackPageView(canonicalPath, previousPath) {
    if (!hasConsent('analytics')) return;
    if (!this.initialized) this._init();
    // Only manually fire page_view for in-app navigation (SPA).
    // The initial page_view after script load is sent automatically by gtag.
    // Note: timeout lets the page <title> update before gtag reads it.
    if (previousPath && window.gtag) {
      window.setTimeout(() => {
        window.gtag('event', 'page_view', {
          page_path: canonicalPath,
        });
      }, 300);
    }
  }
}

/**
 * Facebook Pixel handler.
 *
 * Gated by marketing consent (UK GDPR / PECR).
 * connect.facebook.net/en_US/fbevents.js is NEVER loaded before marketing consent.
 * Subscribes to onConsentChange so late-grant starts tracking and withdrawal revokes.
 * Fully disabled when REACT_APP_FACEBOOK_PIXEL_ID is unset.
 */
export class FacebookPixelHandler {
  constructor(pixelId) {
    this.pixelId = pixelId;
    this.loaded = false;

    if (typeof window === 'undefined' || !pixelId) return;

    if (hasConsent('marketing')) {
      this._init();
    }

    onConsentChange(record => {
      if (record.marketing && !this.loaded) {
        this._init();
      } else if (!record.marketing && this.loaded) {
        this._revoke();
      }
    });
  }

  _init() {
    if (this.loaded || typeof window === 'undefined' || !this.pixelId) return;
    this.loaded = true;

    // Standard Facebook Pixel base code (inline loader).
    // Inlined here so connect.facebook.net is never fetched without consent.
    /* eslint-disable */
    (function(f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */

    window.fbq('init', this.pixelId);
    window.fbq('track', 'PageView');
  }

  _revoke() {
    if (typeof window === 'undefined' || !window.fbq) return;
    window.fbq('consent', 'revoke');
  }

  trackPageView(canonicalPath, previousPath) {
    if (!hasConsent('marketing') || !this.loaded) return;
    if (previousPath && window.fbq) {
      window.setTimeout(() => {
        window.fbq('track', 'PageView');
      }, 300);
    }
  }
}
